// Tells the web app whether the iOS new-user tutorial is running right now,
// and which beat it is on.
//
// **Why this exists.** The tour's coach-marks are NATIVE overlays drawn on top
// of the WKWebView, so the host tears them down whenever a web tool window
// opens — see `iosWebToolBridge.js`. That is correct (an overlay drawn over the
// user's work is worse than silence) but it makes every web-routed tool a dead
// stretch where the tour has nothing to say. The web side can speak there; it
// just had no way to know a tour was running.
//
// **What already crossed, and why it isn't enough.** The viewer URL carries
// `?onboarding=true`, but that is a load-time snapshot: orbit ↔ first person
// does not reload the page (`canBridgeNavigationModeChange`), and the BEAT —
// the thing that says which panel's demo is due — never crossed at all. The
// host keeps it entirely native, in `OnboardingManager.tutorialBeat`.
//
// Modelled on `iosNavigationModeBridge` / `iosRoomBridge`: a CustomEvent the
// host dispatches, and a subscribe helper with a useEffect-shaped teardown.
// One direction only — nothing here reports back, because the existing
// `webToolCompleted` channel already covers "the tool did its job".

const HOST_TO_WEB_EVENT = 'tutorialStateFromHost';

/**
 * The last state the host pushed, so a component mounting mid-tour doesn't have
 * to wait for the next change to learn there is one.
 *
 * The host pushes on every beat change AND once when the viewer finishes
 * loading, so this is populated before any tool window can be opened. Seeded
 * inactive rather than null: "we have not heard from a host" and "there is no
 * host" want the same behaviour, which is to show nothing.
 */
let lastKnownState = {
  active: false,
  beat: null,
  persona: null,
  beatsSeen: [],
  // The host wants the open tool window closed — see `pushTutorialStateToWeb`.
  closeHint: false,
  // A running lesson is asking for this panel's own walkthrough by name.
  lessonDemo: null,
};

if (typeof window !== 'undefined') {
  // Ask the host to send it. The host DOES push on `didFinish`, but that fires
  // when the page finishes loading — which can be before this module has
  // evaluated and installed the listener below. The push is then lost, and
  // nothing re-sends until the next beat CHANGE. Re-entering a scan mid-beat is
  // exactly that case: the tour is sitting on one beat, the page reloads, and
  // every beat-gated web affordance goes dark until the user advances. Seen on
  // device 2026-09-16 (the QR-sheets hint vanished after leaving and returning).
  const requestHostTutorialState = () => {
    window.webkit?.messageHandlers?.tutorialStateRequested?.postMessage({});
  };

  window.addEventListener(HOST_TO_WEB_EVENT, (event) => {
    lastKnownState = {
      active: !!event?.detail?.active,
      beat: event?.detail?.beat ?? null,
      persona: event?.detail?.persona ?? null,
      // The beats this user has already STOOD ON, pushed by the host.
      //
      // Grows as the tour advances, so a panel left open while the tour reaches
      // the beat its walkthrough is gated on sees this change.
      //
      // A membership list, deliberately not the tour's running order. The host
      // owns it because only the host knows where the user has been, and
      // "have they been through step X" survives a tour that moves backwards —
      // which it does in four places — where comparing positions does not.
      beatsSeen: Array.isArray(event?.detail?.beatsSeen)
        ? event.detail.beatsSeen
        : [],
      closeHint: !!event?.detail?.closeHint,
      lessonDemo: event?.detail?.lessonDemo || null,
    };
  });

  // Listener first, then ask — the other order can drop the answer.
  requestHostTutorialState();
}

/** The tour's state as of the host's last push. Never null. */
export function getHostTutorialState() {
  return lastKnownState;
}

/**
 * Is the tour RUNNING and currently on `beat`?
 *
 * Both halves are required, and the pairing is the whole point of the helper:
 * the host pushes `tutorialBeat` on every load whether or not the tour is
 * active, and that beat is persisted across launches. So a bare
 * `getHostTutorialState().beat === x` stays true forever for anyone who
 * skipped the tour while parked on `x`. Ask this instead.
 *
 * @param {string} beat
 * @returns {boolean}
 */
/**
 * The persona the host recorded, or null when no host has spoken.
 *
 * Null is NOT "contractor". A caller deciding what to teach must treat an
 * unknown persona as "teach nothing persona-specific" rather than guessing —
 * the host's own getter defaults to `.contractor` when unset, and copying that
 * default over here would silently show a homeowner contractor copy.
 */
export function getHostTutorialPersona() {
  return lastKnownState.persona;
}

export function isHostTutorialAtBeat(beat) {
  return lastKnownState.active && lastKnownState.beat === beat;
}

/**
 * Is the tour RUNNING, and has the user already been through `beat`?
 *
 * The difference `isHostTutorialAtBeat` cannot express, and the difference
 * between a walkthrough that explains a panel whenever the user opens it and
 * one locked shut because they opened it a step late.
 *
 * Asks MEMBERSHIP, never position. The host sends the set of beats stood on, so
 * this survives the tour moving backwards: a user sent back to an earlier step
 * by a failed render keeps everything they have already seen. Comparing indices
 * in a pushed running order would silently take it away from them, and would put
 * a global ordering on a wire nothing type-checks.
 *
 * The current beat counts as seen — the host stamps it on arrival.
 *
 * Falls back to an exact match when the host sends no set (an older build).
 * Exact is the pre-existing behaviour, so an unknown history can only ever
 * withhold a walkthrough, never play one at the wrong time.
 *
 * @param {string} beat
 * @returns {boolean}
 */
export function hasHostTutorialSeenBeat(beat) {
  if (!lastKnownState.active) return false;
  const { beat: current, beatsSeen } = lastKnownState;
  if (!beatsSeen.length) return current === beat;
  // `|| current === beat` so "never withholds relative to the old exact-match
  // gate" holds by construction rather than by host discipline. Membership
  // normally contains the current beat already — the host stamps it on arrival —
  // but that is only true once the beat has been WRITTEN on this install, and a
  // history unioned from the server could pair with a beat still coming from the
  // getter's fallback.
  return beatsSeen.includes(beat) || current === beat;
}

/**
 * Subscribe to tutorial-state pushes from the iOS host.
 *
 * Fires once immediately with the current state, so a panel that mounts after
 * the host's push still learns about it — the alternative is a panel that opens
 * mid-tour and stays silent because it happened to miss one event.
 *
 * @param {(state: {active: boolean, beat: string|null, persona: string|null,
 *   closeHint: boolean, lessonDemo: string|null})
 *   => void} callback
 * @returns {() => void} unsubscribe, suitable for a useEffect cleanup
 */
export function subscribeToHostTutorialState(callback) {
  // Hands over the SAME shape the module caches, rather than rebuilding it.
  // Rebuilt, this dropped `persona` from every push while the seed call below
  // — which passes `lastKnownState` whole — delivered it once: a subscriber
  // would read the persona correctly on mount and `undefined` forever after.
  const handler = () => callback(lastKnownState);
  window.addEventListener(HOST_TO_WEB_EVENT, handler);
  callback(lastKnownState);
  return () => window.removeEventListener(HOST_TO_WEB_EVENT, handler);
}

export default subscribeToHostTutorialState;
